import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { AncillaryWorkflowPage } from 'src/shared/models/workflow-page-ancllary.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { PageInterface } from 'src/app/workflow/planner/page/page.interface';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { TotalUosByBlock } from 'src/app/workflow/base-classes/total-uos-by-block.component';
import { TotalUOSShiftRecordService } from 'src/shared/services/shift-record-total-uos.service';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';

@Component({
  selector: 'smt-enter-reason-uos',
  templateUrl: './enter-reason-uos.component.html',
  styleUrls: ['./enter-reason-uos.component.scss']
})
export class TotalUosEnterReasonComponent extends TotalUosByBlock implements OnInit, PageInterface {
    @Input() page: AncillaryWorkflowPage;
    @Output() previousRequested = new EventEmitter();

    private enterUOSReasonForm: UntypedFormGroup = new UntypedFormGroup({});
    public formGroups: UntypedFormGroup[];
    loading: boolean;
    
    constructor(
        protected _router: Router, 
        protected _shiftBlockService: ShiftBlockService,
        protected _totalUosShiftRecordService: TotalUOSShiftRecordService,
        protected _workflowUtilityService: WorkflowUtilityService) { 
        super(_router, _shiftBlockService, _totalUosShiftRecordService, _workflowUtilityService);  
    }

    ngOnInit() {            
        this.formGroups = [ this.enterUOSReasonForm ];

        super.initialize(this.page, () => this.initializeForm());
    }
    
    private async initializeForm() {
        super.initializeShiftRecords(false).then(() => {
                // Doing this will trigger the form's changes subscription which will force a form validation.
                // This is necessary because the child components of this form are not triggering the proper
                // form change subscription once the data is all loaded.
                this.enterUOSReasonForm.updateValueAndValidity();
            }            
        );
    }

    public isFormValid(): boolean { 
        // If the form is still loading then do not evaluate.
        if (this.loadingRecords || !this.enterUOSReasonForm || !this.enterUOSReasonForm.controls) {    
            return false;
        }                    
        
        let shiftBlocksWithMeasures: number[] = [];
        let shiftBlocksWithoutMeasures: number[] = [];
        let shiftBlocksWithReasons: number[] = [];
        let shiftBlocksWithoutReasons: number[] = [];
        let containMeasures = false;

        Object.keys(this.enterUOSReasonForm.controls).filter(c => c.startsWith('list') || c.startsWith('measure')).forEach(controlName => {                   
            let controlValue = this.enterUOSReasonForm.controls[controlName].value;            

            // If the control contains list (which is the list of reasons) then check that shift block id.
            if (controlName.startsWith('list')) {
                let shiftBlockId = +controlName.split('-')[2]; 

                // If there are reasons added to the shift block then mark that shift block id.
                if (MiscUtil.checkArray(controlValue) && !shiftBlocksWithReasons.some(n => n == shiftBlockId)) {
                    shiftBlocksWithReasons.push(shiftBlockId);
                }
                // If there are not reasons added to the shift block then mark that shift block id.
                else if (!MiscUtil.checkArray(controlValue) && !shiftBlocksWithoutReasons.some(n => n == shiftBlockId)) {
                    shiftBlocksWithoutReasons.push(shiftBlockId);
                }
            }    
            
            // If the control contains measures then check that shift block id.
            if (controlName.startsWith('measure')) {
                containMeasures = true;
                let shiftBlockId = +controlName.split('-')[3]; 

                // If there are measures added to the shift block then mark that shift block id.
                if (MiscUtil.isValidNumber(controlValue) && !shiftBlocksWithMeasures.some(n => n == shiftBlockId)) {
                    shiftBlocksWithMeasures.push(shiftBlockId);
                }   
                // If there are not measures added to the shift block then mark that shift block id.
                else if (!MiscUtil.isValidNumber(controlValue) && !shiftBlocksWithoutMeasures.some(n => n == shiftBlockId)) {
                    shiftBlocksWithoutMeasures.push(shiftBlockId);
                }
            }
        });

        let shiftBlocksWithVariance: number[];
        
        // If there are shift records and they all have staffing plans then we can find which ones have a variance.
        if (MiscUtil.checkArray(this.shiftRecords) &&
            this.shiftRecords.length > 0 &&
            !this.shiftRecords.some(s => !MiscUtil.checkArray(s.staffingPlans) || s.staffingPlans.length == 0)) {
            shiftBlocksWithVariance = this.shiftRecords.map(s => s.staffingPlans)
                                                       .reduce((s, value) => s.concat(value))
                                                       .filter(s => s.amount != s.recommended)
                                                       .map(s => s.shiftBlockId);

            if (MiscUtil.checkArray(shiftBlocksWithVariance) && shiftBlocksWithVariance.length > 0) {
                // Prevent duplicate shift block ids from being checked.
                shiftBlocksWithVariance = shiftBlocksWithVariance.filter((shiftBlockId, index, array) => {
                    return array.indexOf(array.find(t => t === shiftBlockId)) === index;
                });
            }            
        }                   

        // Make sure all required reasons and all required measures have been entered.
        let requiredReasonsEntered = !MiscUtil.checkArray(shiftBlocksWithVariance) || MiscUtil.areArraysEqual(shiftBlocksWithVariance, shiftBlocksWithReasons);
        let requiredMeasuresEntered = !containMeasures || (!MiscUtil.checkArray(shiftBlocksWithoutMeasures) && shiftBlocksWithoutMeasures.length == 0);

        return !this.hasShiftBlocks() || (requiredReasonsEntered && requiredMeasuresEntered); 
    }

    public async save(): Promise<number> {
        this.setStatus();

        let shiftRecordsToSave = this.shiftRecords.filter(s => 
            (MiscUtil.checkArray(s.varianceReasonRecords) && s.varianceReasonRecords.length > 0) || 
            (MiscUtil.checkArray(s.measureRecords) && s.measureRecords.length > 0));

        if (MiscUtil.checkArray(shiftRecordsToSave) && shiftRecordsToSave.length > 0) {
            let ids = await this._totalUosShiftRecordService.setShiftRecordsWithAssociated(shiftRecordsToSave);
            let id = await this._totalUosShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);

            // Validate the ids returned.  If they aren't what was expected then return null which will trigger an error and prevent
            // the user from proceeding to the next work flow page.
            if (!MiscUtil.checkArray(ids) || ids.length != shiftRecordsToSave.length || !MiscUtil.isValidNumber(id)) {
                return null;
            }
        }

        return this.shiftRecord.id;
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.EnterReasons;
        }
    }

    public shouldSkipSave(): boolean {
        return false;
    }
}
